import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/design-system/Button'
import { Card, Skeleton } from '@/design-system/primitives'
import { ApiError } from '@/lib/apiClient'
import { fromZonedInput, toZonedInput } from '@/lib/datetime'

import { useAdminMutation, useAdminQuery } from '../api'
import type { CakeMoment, I18n, ProgramItem } from '../types'
import { I18nField, SelectField, SwitchField, TextField, type I18nValue } from './fields'
import { MediaField } from './MediaField'

/**
 * 🎂 Le LeYAMO Cake Moment — le seul instant où l'écran de la salle cesse
 * d'être un programme.
 *
 * Le serveur savait tout faire (textes, visuel, étape du déroulé qui le
 * déclenche) ; seul l'écran pour le régler manquait. Une étape « Cake Moment »
 * du déroulé suffit à le déclencher ; le rattacher à une étape précise sert
 * quand il y en a plusieurs ou quand l'étape porte un autre type.
 */
export function CakeMomentPanel({
  eventId,
  timezone,
  programItems,
  label,
  canEdit,
}: {
  eventId: string | undefined
  timezone: string
  programItems: ProgramItem[]
  label: (value: I18n) => string
  canEdit: boolean
}) {
  const { t } = useTranslation()
  const path = `/admin/events/${eventId}/cake-moment`
  const query = useAdminQuery<CakeMoment>(['admin-cake-moment', eventId], path, eventId)
  const save = useAdminMutation<CakeMoment, Record<string, unknown>>('put', () => path, eventId, [
    ['admin-cake-moment', eventId],
    ['public-cake'],
  ])

  const [name, setName] = useState<I18nValue>({})
  const [description, setDescription] = useState<I18nValue>({})
  const [message, setMessage] = useState<I18nValue>({})
  const [mediaId, setMediaId] = useState<string | null>(null)
  const [programItem, setProgramItem] = useState('')
  const [scheduledAt, setScheduledAt] = useState('')
  const [active, setActive] = useState(false)
  const [notes, setNotes] = useState('')

  // Le formulaire part de ce qui est enregistré, une fois chargé — puis ne
  // bouge plus pendant la saisie, même quand la requête se relit.
  const loaded = query.data
  useEffect(() => {
    if (!loaded) return
    setName(loaded.name_i18n)
    setDescription(loaded.description_i18n)
    setMessage(loaded.message_i18n)
    setMediaId(loaded.media_id)
    setProgramItem(loaded.program_item_id ?? '')
    setScheduledAt(toZonedInput(loaded.scheduled_at, timezone))
    setActive(loaded.is_active)
    setNotes(loaded.notes ?? '')
  }, [loaded, timezone])

  return (
    <Card className="space-y-5">
      <div>
        <h2 className="font-display text-heading-md text-content-primary">
          {t('admin.cake.title')}
        </h2>
        <p className="mt-1 text-sm text-content-secondary">{t('admin.cake.hint')}</p>
      </div>

      {query.isPending ? <Skeleton className="h-24 w-full" /> : null}

      {loaded ? (
        <>
          <div className="grid gap-5 sm:grid-cols-2">
            <I18nField label={t('admin.cake.name')} value={name} onChange={setName} />
            <I18nField
              label={t('admin.cake.message')}
              hint={t('admin.cake.messageHint')}
              multiline
              value={message}
              onChange={setMessage}
            />
            <I18nField
              label={t('admin.food.descriptionField')}
              multiline
              value={description}
              onChange={setDescription}
            />
            <MediaField
              label={t('admin.cake.media')}
              value={mediaId}
              eventId={eventId}
              aspect="wide"
              onChange={setMediaId}
            />
            <SelectField
              label={t('admin.cake.programItem')}
              hint={t('admin.cake.programItemHint')}
              value={programItem}
              onChange={setProgramItem}
              options={[
                { value: '', label: t('admin.cake.anyCakeStep') },
                ...programItems.map((item) => ({ value: item.id, label: label(item.title_i18n) })),
              ]}
            />
            <TextField
              label={t('admin.cake.scheduledAt')}
              hint={t('admin.cake.scheduledAtHint', { timezone })}
              type="datetime-local"
              value={scheduledAt}
              onChange={setScheduledAt}
            />
            <SwitchField
              label={t('admin.cake.active')}
              hint={t('admin.cake.activeHint')}
              value={active}
              onChange={setActive}
            />
            <TextField
              label={t('admin.cake.notes')}
              hint={t('admin.cake.notesHint')}
              value={notes}
              onChange={setNotes}
            />
          </div>

          {canEdit ? (
            <div className="flex flex-wrap items-center gap-3">
              <Button
                size="sm"
                disabled={save.isPending}
                onClick={() =>
                  save.mutate({
                    name_i18n: name,
                    description_i18n: description,
                    message_i18n: message,
                    media_id: mediaId,
                    program_item_id: programItem || null,
                    scheduled_at: fromZonedInput(scheduledAt, timezone),
                    is_active: active,
                    notes: notes.trim() || null,
                  })
                }
              >
                {save.isPending ? t('state.loading') : t('admin.save')}
              </Button>
              {save.isSuccess ? (
                <span className="text-caption text-state-success">{t('admin.cake.saved')}</span>
              ) : null}
            </div>
          ) : (
            <p className="text-caption text-content-muted">{t('admin.readOnly')}</p>
          )}
        </>
      ) : null}

      {(save.error ?? query.error) instanceof ApiError ? (
        <p className="text-sm text-state-danger">{(save.error ?? query.error)?.message}</p>
      ) : null}
    </Card>
  )
}
